import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { BudgetProposalT } from 'routes/BudgetModule/config/types';
import AuxiliaryTabs from 'components/SharedComponents/AuxiliaryTabs/AuxiliaryTabs';
import { styled } from '@mui/material/styles';
import { Box, Stack } from '@mui/material';
import { BudgetExpenseOrIncome, BudgetSections } from 'business/constants';
import { TRACKABLE_EVENTS, trackEventBudget } from 'helpers/trackerHelpers';
import NavigatingDropDownMenu from 'ui/NavigatingDropDownMenu/NavigatingDropDownMenu';
import {
    ALL_PROJECTION_FILE_PREFIX,
    exportGridsToExcel,
    getExcelFilename,
} from 'routes/BudgetModule/contexts/helpers';
import ExportButtonMultipleGrid from 'components/ExportButton/ExportButtonMultipleGrid';
import { useGridApiContext } from 'routes/BudgetModule/contexts/GridApiContext';
import { useFlatExport } from 'routes/BudgetModule/helpers/hooks/useFlatExport';
import ProjectionDaf from './Components/ProjectionDaf';

type Props = {
    budgetProposals: BudgetProposalT[];
};

const StyGrid = styled(Box, {
    name: 'StyGrid',
})({
    padding: '0 3rem 3rem 3rem',
    '& .ag-row-group': {
        display: 'flex',
        alignItems: 'center',
    },
    '& .ag-cell': {
        display: 'flex',
        alignItems: 'center',
        border: 'none',
        lineHeight: '16px',
        fontSize: '11px',
        '&.ag-right-aligned-cell': {
            justifyContent: 'flex-end',
        },
    },
});

type ProjectionTypes = 'atterrissage' | 'pluriannual';

export default function AllProjections(props: Props): React.ReactElement {
    const { t } = useTranslation();
    const { budgetProposals } = props;
    const {
        tab,
        expenseOrIncome = BudgetExpenseOrIncome.EXPENSE,
        projectionType = 'atterrissage',
    } = useParams<{
        tab: string;
        expenseOrIncome: string;
        projectionType: ProjectionTypes;
    }>();

    const [gridContext] = useGridApiContext();
    const fileName = getExcelFilename(ALL_PROJECTION_FILE_PREFIX, [
        expenseOrIncome,
        projectionType,
    ]);
    const exportFunction = () =>
        exportGridsToExcel(gridContext[fileName], fileName);
    const { shouldShowOpenedGroups, setShouldShowOpenedGroups } =
        useFlatExport(exportFunction);

    useEffect(() => {
        let trackEvent: string | null = null;
        if (projectionType === 'atterrissage') {
            trackEvent =
                TRACKABLE_EVENTS.budget.budgetOverview.projections.atterrissage
                    .pageOpened;
        } else if (projectionType === 'pluriannual') {
            trackEvent =
                TRACKABLE_EVENTS.budget.budgetOverview.projections.pluriannual
                    .pageOpened;
        }
        if (trackEvent !== null) {
            trackEventBudget(trackEvent);
        }
    }, [projectionType]);

    const exportButton = (
        <ExportButtonMultipleGrid
            event={
                TRACKABLE_EVENTS.budget.budgetOverview.projections[
                    projectionType
                ]?.export
            }
            fileName={fileName}
            setShouldShowOpenedGroups={setShouldShowOpenedGroups}
        />
    );

    const tabsList = [
        {
            label: t('budget.expense'),
            pathname: `/budget_overview/${tab}/expense/${projectionType}`,
        },
        {
            label: t('budget.income'),
            pathname: `/budget_overview/${tab}/income/${projectionType}`,
        },
    ].map((tabItem) => ({
        ...tabItem,
        key: tabItem.label,
        tabActions: (
            <NavigatingDropDownMenu
                tabsList={[
                    {
                        label: t('projection.atterrissageProjection'),
                        pathname: `/budget_overview/${tab}/${expenseOrIncome}/atterrissage`,
                    },
                    {
                        label: t('projection.pluriannualProjection'),
                        pathname: `/budget_overview/${tab}/${expenseOrIncome}/pluriannual`,
                    },
                ]}
            >
                {exportButton}
            </NavigatingDropDownMenu>
        ),
        children: (
            <Stack spacing={6}>
                {[
                    BudgetSections.OPERATING,
                    BudgetSections.INVESTMENT,
                    null,
                ].map((section: BudgetSections | null) => (
                    <ProjectionDaf
                        expenseOrIncome={
                            expenseOrIncome.toUpperCase() as BudgetExpenseOrIncome
                        }
                        section={section}
                        key={section}
                        budgetProposal={
                            budgetProposals.filter(
                                (bp) => bp.section === section,
                            )[0]
                        }
                        shouldShowOpenedGroups={shouldShowOpenedGroups}
                    />
                ))}
            </Stack>
        ),
        disabled: false,
    }));

    return (
        <StyGrid>
            <AuxiliaryTabs tabsList={tabsList} />
        </StyGrid>
    );
}
